/**
 * Finding one source among the workspace's, and asking the table a question.
 *
 * The Sources screen draws every Source Profile in the workspace in the one
 * order the server picked, and a profile is per league **and** per season — so
 * the list grows by a row for every league the portal ingests, every year, and
 * every row of it stays on screen for ever. Three rows is the current
 * workspace; eighteen was one season of one publisher, and `Dashboard`'s bulk
 * delete exists because retiring them was eighteen visits to eighteen screens.
 *
 * So: a term, two facets, and an order. Each is a string or number operation
 * over the list the screen already has, which is the point of this module
 * existing at all — it holds no React and no DOM, so `tests/source-list.test.ts`
 * can run it under bare `node` (`client-feedback-round-2/04`), and the rules
 * below are assertions rather than claims about some JSX.
 *
 * **What is searched is what is printed.** `stat-search.ts` states the rule and
 * this keeps it: a row matches on its name, its sport, its league, its season
 * and who built it, which is five of the eight things its row shows. The two it
 * does not match on — the last Run's status and when it last succeeded — are
 * the two the sort is for, and both are better asked as an ordering than as a
 * word. Nothing invisible is matched: a row that appears for a reason the
 * reader cannot see on it is a row they have to guess about.
 *
 * **The server's order is the default, and it is reachable again.** The API
 * sorts failing sources first, which is the one ordering that answers *what
 * needs me*; a table that can be sorted away from it but not back has quietly
 * lost it. A third click on the same header restores it.
 */

import type { SourceProfile } from "./api";

// --- The text rule ---------------------------------------------------------

/**
 * Case, spacing and punctuation folded away.
 *
 * **This is `stat-search.ts`'s `normalise`, copied rather than imported, and
 * the copy is forced.** `npm test` runs these modules through bare
 * `node --experimental-strip-types`, which resolves ES modules the way Node
 * does — by file, extension included. Every import inside `src/` is written
 * extensionless for Vite, and `tsconfig.json` sets
 * `allowImportingTsExtensions: false`, so a runtime import from one `src`
 * module to another makes the importing module unloadable under the test
 * runner. `selection.ts` is written with no imports for exactly this reason and
 * says so; this is the same constraint reached from the other side.
 *
 * The copy is pinned rather than trusted: `tests/source-list.test.ts` drives
 * both modules' folding over the same inputs and fails if they ever disagree.
 * Exported for that test alone.
 */
export function fold(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * A typed query as the terms it holds — `stat-search.ts`'s `terms`, copied
 * under the same constraint and pinned by the same test.
 *
 * Split rather than folded whole, so the terms may be matched in any order:
 * "soccer usl" finds a source by its sport and its league together, which one
 * folded string could not.
 */
export function words(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((term) => term !== "");
}

// --- Narrowing -------------------------------------------------------------

/** A typed term and the two facets, as the screen holds them. */
export interface Narrowing {
  query: string;
  /** A sport, or the empty string for every sport. */
  sport: string;
  /** A league, or the empty string for every league. */
  league: string;
}

/** Narrowing by nothing: what the screen opens on. */
export const EVERYTHING: Narrowing = { query: "", sport: "", league: "" };

/**
 * The narrowing a page address asks for: `/sources?sport=soccer&league=mls`.
 *
 * The address is where the Dashboard's sport rows send people, so a link can
 * open the Sources list already filtered. Missing or empty parameters mean
 * "every", which is what makes `/sources` itself the whole list.
 */
export function narrowingFromAddress(params: URLSearchParams): Narrowing {
  return {
    query: params.get("q") ?? "",
    sport: params.get("sport") ?? "",
    league: params.get("league") ?? "",
  };
}

/**
 * The address parameters for a narrowing — only those set, so an unfiltered
 * list is plain `/sources` rather than `/sources?q=&sport=&league=`.
 */
export function addressFor(narrowing: Narrowing): Record<string, string> {
  const params: Record<string, string> = {};
  if (narrowing.query !== "") params.q = narrowing.query;
  if (narrowing.sport !== "") params.sport = narrowing.sport;
  if (narrowing.league !== "") params.league = narrowing.league;
  return params;
}

/**
 * Whether anything is being hidden on purpose.
 *
 * The screen needs this to say so. The tiles above the table count the rows
 * below them, so a filtered table has to admit it is filtered or the tiles
 * become a wrong answer to "how many sources are there" — and a search that
 * matched nothing has to read as a search that matched nothing rather than as
 * an empty workspace.
 */
export function isNarrowed(narrowing: Narrowing): boolean {
  return words(narrowing.query).length > 0 || narrowing.sport !== "" || narrowing.league !== "";
}

/**
 * The sources a narrowing keeps, in the order they were given.
 *
 * Returns the **same array** when nothing was dropped, as `selection.ts`'s
 * `retain` does and for the same reason: this runs on every render of a screen
 * that polls every four seconds, and a fresh array each time restarts every
 * memo keyed on the list.
 */
export function narrow(
  profiles: readonly SourceProfile[],
  narrowing: Narrowing,
): readonly SourceProfile[] {
  // A query of nothing but spaces and punctuation yields no terms and is
  // therefore not a filter — which is what keeps a stray keystroke from
  // emptying the table.
  const wanted = words(narrowing.query);
  if (wanted.length === 0 && narrowing.sport === "" && narrowing.league === "") return profiles;

  const kept = profiles.filter((profile) => {
    if (narrowing.sport !== "" && profile.sport !== narrowing.sport) return false;
    if (narrowing.league !== "" && profile.league !== narrowing.league) return false;
    if (wanted.length === 0) return true;
    const text = searchable(profile);
    // Every term, anywhere in the row: the terms may land in different fields,
    // so "soccer usl" finds a league by its sport and its name together, which
    // one folded string could not.
    return wanted.every((term) => text.some((value) => value.includes(term)));
  });
  return kept.length === profiles.length ? profiles : kept;
}

/**
 * The strings one row can be found by — each of them a string the row prints.
 *
 * The season is included as its digits: a workspace holds the same league for
 * several years, and "2025" is the shortest way to ask for last season's. Who
 * built it is included because everyone sees every source (ADR-0016) and
 * "mine" is a question the column exists to answer.
 */
function searchable(profile: SourceProfile): readonly string[] {
  return [
    fold(profile.name),
    fold(profile.sport),
    fold(profile.league),
    String(profile.season),
    fold(profile.created_by ?? ""),
  ].filter((value) => value !== "");
}

/** Each sport the list holds, once, in alphabetical order. */
export function sportsOf(profiles: readonly SourceProfile[]): string[] {
  return [...new Set(profiles.map((profile) => profile.sport))].sort(compareText);
}

/**
 * The leagues to offer, given the sport already chosen.
 *
 * Scoped to that sport rather than listing every league in the workspace,
 * because two filters that can contradict each other produce an empty table and
 * no account of why. With no sport chosen this is every league, which is the
 * same rule with the scope left open.
 */
export function leaguesFor(profiles: readonly SourceProfile[], sport: string): string[] {
  const within = sport === "" ? profiles : profiles.filter((profile) => profile.sport === sport);
  return [...new Set(within.map((profile) => profile.league))].sort(compareText);
}

/**
 * The options a `<select>` is given, with the value it is currently set to
 * guaranteed among them.
 *
 * **A `<select>` whose value matches none of its options renders as the first
 * option**, silently — it is this frontend's most-repeated defect, found three
 * times (`field-mapping-usability/01` has the list). These two menus are built
 * from the list itself, and the list polls: filter to one sport, have somebody
 * delete the last source in it, and four seconds later the control that says
 * *soccer* is showing *football* while the table below it is empty.
 *
 * So a set value that has left the data is kept on the menu. It leads, because
 * the one option that no longer describes anything is the one worth meeting
 * first, and choosing anything else drops it for good.
 */
export function offering(options: readonly string[], chosen: string): string[] {
  if (chosen === "" || options.includes(chosen)) return [...options];
  return [chosen, ...options];
}

/**
 * Choosing a sport, and what it does to the league beside it.
 *
 * A league belongs to exactly one sport, so changing the sport usually strands
 * the league on a value the new sport has no row for — an empty table caused by
 * a control the reader did not touch. The league is therefore dropped unless
 * the new sport actually has it, which is the arithmetic the screen would
 * otherwise have to do in an effect.
 */
export function chooseSport(
  profiles: readonly SourceProfile[],
  narrowing: Narrowing,
  sport: string,
): Narrowing {
  const available = leaguesFor(profiles, sport);
  return {
    ...narrowing,
    sport,
    league: available.includes(narrowing.league) ? narrowing.league : "",
  };
}

// --- Ordering --------------------------------------------------------------

/** The columns of the Sources table that can be ordered. */
export type SortColumn =
  | "name"
  | "created_by"
  | "sport"
  | "league"
  | "season"
  | "last_run"
  | "last_success"
  | "players";

export type SortDirection = "asc" | "desc";

/** A column and a direction, or `null` for the order the server sent. */
export interface Sort {
  column: SortColumn;
  direction: SortDirection;
}

/**
 * Which way a column opens on its first click.
 *
 * Not a uniform "ascending first". Somebody clicking **Players** is asking
 * which source publishes the most, and somebody clicking **Last success** is
 * asking which ran most recently — opening those ascending answers the
 * opposite question and costs a second click every time. Names and leagues
 * open ascending, because A-first is what a name column is read for.
 */
export function defaultDirection(column: SortColumn): SortDirection {
  switch (column) {
    case "season":
    case "players":
    case "last_success":
      return "desc";
    default:
      return "asc";
  }
}

/**
 * What clicking a column header does: open it, flip it, then let it go.
 *
 * Three states rather than two, and the third is the whole reason: the server's
 * failing-first order is a real ordering with information in it, and a header
 * that only toggled between ascending and descending would make it unreachable
 * without a page reload.
 */
export function nextSort(current: Sort | null, column: SortColumn): Sort | null {
  if (current === null || current.column !== column) {
    return { column, direction: defaultDirection(column) };
  }
  if (current.direction === defaultDirection(column)) {
    return { column, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return null;
}

/**
 * The list in the order a sort asks for, or exactly as given when there is none.
 *
 * Copied before sorting — `Array.prototype.sort` works in place, and the array
 * here is React Query's cached payload. Sorting it where it lies would reorder
 * the cache under every other reader of it.
 */
export function sortSources(
  profiles: readonly SourceProfile[],
  sort: Sort | null,
): readonly SourceProfile[] {
  if (sort === null) return profiles;
  const sign = sort.direction === "asc" ? 1 : -1;
  return [...profiles].sort((left, right) => {
    const a = valueOf(left, sort.column);
    const b = valueOf(right, sort.column);
    /**
     * Absence sorts last whichever way the column runs.
     *
     * A source that has never run has no player count and no last success, and
     * there is no direction in which "no answer" is the answer somebody wants
     * at the top. Outside the `sign` below deliberately: flipping the column
     * must not flip this.
     */
    if (a === null || b === null) {
      if (a === b) return 0;
      return a === null ? 1 : -1;
    }
    const order =
      typeof a === "number" && typeof b === "number" ? a - b : compareText(String(a), String(b));
    // A zero leaves the pair as it was. `sort` is stable by specification, so
    // rows a coarse column cannot tell apart — four distinct statuses over
    // twenty rows — keep the server's failing-first order among themselves
    // rather than shuffling on every poll.
    return order * sign;
  });
}

/**
 * What a column sorts on, or `null` when the row has no answer for it.
 *
 * Numbers come back as numbers: lexically 9 falls after 10, and a Players
 * column that said so would be wrong about the one thing it is for.
 */
function valueOf(profile: SourceProfile, column: SortColumn): string | number | null {
  switch (column) {
    case "name":
      return profile.name;
    case "created_by":
      return profile.created_by;
    case "sport":
      return profile.sport;
    case "league":
      return profile.league;
    case "season":
      return profile.season;
    case "last_run":
      return profile.last_run ? statusRank(profile.last_run.status) : null;
    case "last_success":
      /**
       * Parsed to an instant, **not compared as the text the API sends.**
       *
       * ISO timestamps do sort chronologically as plain strings, but the
       * comparison below is a locale collator with `numeric: true` on — which
       * reads a run of digits as a number, and the fractional second is a run
       * of digits whose *length varies with the value*. The API prints
       * `…:00.900Z` and `…:00.123456Z` for two Runs in the same second, the
       * collator compares 900 against 123456, and the later Run sorts first.
       * Measured, not reasoned about: `Intl.Collator` returns -1 for that pair.
       *
       * A number cannot be read two ways, so this converts rather than
       * arranging for the right comparator to be chosen. An unparseable value
       * is an absence, which sorts to the bottom with the sources that have
       * never succeeded rather than to whichever end `NaN` happens to fall.
       */
      if (profile.last_successful_run_at === null) return null;
      const at = Date.parse(profile.last_successful_run_at);
      return Number.isNaN(at) ? null : at;
    case "players":
      return profile.last_run?.player_count ?? null;
  }
}

/**
 * How far up the Last Run column a status belongs.
 *
 * Not alphabetical, which would file `failed` under F between `cancelled` and
 * `queued` and tell the reader nothing. Ascending is most-wanting-attention
 * first — the server's own failing-first rule, applied to the whole column
 * rather than to the top of it. A source that has never run has no status at
 * all and sorts with the other absences, at the bottom.
 */
function statusRank(status: string): number {
  const rank = STATUS_ORDER.indexOf(status);
  // A status this frontend has not been taught sorts after the ones it has,
  // rather than silently sharing a rank with `failed` at index 0.
  return rank === -1 ? STATUS_ORDER.length : rank;
}

const STATUS_ORDER = ["failed", "running", "queued", "cancelled", "skipped", "succeeded"];

/**
 * Text compared the way a reader reads it.
 *
 * `numeric` is what keeps `ncaa_d2` above `ncaa_d10`, and case-insensitivity is
 * what keeps a source somebody named in lower case out of a block of its own
 * below every capitalised one.
 */
const COLLATOR = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

function compareText(left: string, right: string): number {
  return COLLATOR.compare(left, right);
}

// --- Both together ---------------------------------------------------------

/**
 * The rows the table draws: narrowed first, then ordered.
 *
 * In that order because the other one is a different screen — sorting the whole
 * workspace and then hiding most of it would leave the kept rows in the right
 * order by accident, and nothing downstream could tell the two apart. With
 * neither, this is the server's list itself, by identity.
 */
export function arrange(
  profiles: readonly SourceProfile[],
  narrowing: Narrowing,
  sort: Sort | null,
): readonly SourceProfile[] {
  return sortSources(narrow(profiles, narrowing), sort);
}
